import { useEffect, useState } from 'react';
import { atom } from 'jotai';
import { useAtomValue, useSetAtom } from 'jotai/react';
import { ViewBox } from '@view-model/domain/view-model';

// キャンバス外枠 DOM の実測サイズ。null は「まだ計測されていない」ことを表し、
// 消費側はこの状態でズーム計算等を行ってはならない（型でガードを強制する）。
const contentViewBoxAtom = atom<ViewBox | null>(null);

/**
 * キャンバス外枠 DOM を ResizeObserver で継続的に観測して atom に反映する。
 * サイドバー幅・ドロワー幅・window リサイズすべて DOM 側のレイアウトに任せて計測する。
 *
 * 返り値は「観測対象の div にセットする callback ref」。対象 DOM は条件付きレンダーで
 * 後から現れるケースがあるので、useState で DOM 参照を保持して、DOM が確定した時点で
 * useEffect 経由で ResizeObserver を張り直す。対象 DOM が外れたら atom を null（未計測）に戻す。
 * ViewModelPage の 1 箇所からのみ呼ぶこと。
 */
export const useProvideContentViewBox = (): ((el: HTMLDivElement | null) => void) => {
  const setViewBox = useSetAtom(contentViewBoxAtom);
  const [canvasEl, setCanvasEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!canvasEl) return;

    const applySize = (width: number, height: number): void => {
      setViewBox((prev) =>
        prev !== null && prev.width === width && prev.height === height ? prev : new ViewBox(width, height)
      );
    };

    // ResizeObserver の初回コールバックを待たず、mount 直後に同期計測して確定させる。
    const rect = canvasEl.getBoundingClientRect();
    applySize(rect.width, rect.height);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      applySize(width, height);
    });
    observer.observe(canvasEl);
    return () => {
      observer.disconnect();
      setViewBox(null);
    };
  }, [canvasEl, setViewBox]);

  return setCanvasEl;
};

export const useContentViewBox = (): ViewBox | null => useAtomValue(contentViewBoxAtom);
